import { match, P } from 'ts-pattern';

import getOrdinalNumber from '~/domains/contest/domains/gamecenter/domains/player/utils/getOrdinalNumber';
import type { PublicGame } from '~/domains/contest/domains/services/interfaces/propsService/api/game';
import { isPublicGameWithTeams } from '~/domains/contest/domains/services/interfaces/propsService/api/game';

export type GameLine = {
  isLive: boolean;
  isScheduled: boolean;
  homeScore?: number;
  awayScore?: number;
  statusLine?: string;
  dateLine?: string;
};

const MS_PER_DAY = 86_400_000;

const TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const SHORT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const WEEKDAY_FORMAT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

const startOfDay = (timestamp: number): number => {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const formatTime = (timestamp: number): string => TIME_FORMAT.format(timestamp);

const formatShortDate = (timestamp: number): string => SHORT_DATE_FORMAT.format(timestamp);

const formatRelativeDay = (timestamp: number, now: number): string => {
  const dayDelta = Math.round((startOfDay(timestamp) - startOfDay(now)) / MS_PER_DAY);

  return match(dayDelta)
    .with(0, () => 'Today')
    .with(1, () => 'Tomorrow')
    .otherwise(() => WEEKDAY_FORMAT.format(timestamp));
};

const formatLiveClock = (state: PublicGame['state']): string | undefined =>
  match(state)
    .with({ phase: 'overtime' }, () => 'OT')
    .with({ phase: 'shootout' }, () => 'SO')
    .with({ league: P.union('nba', 'nfl', 'ncaaf') }, ({ quarter, clock }) =>
      [`Q${quarter}`, clock].filter(Boolean).join(' ')
    )
    .with({ league: 'ncaab' }, ({ half, clock }) => [`H${half}`, clock].filter(Boolean).join(' '))
    .with({ league: 'nhl' }, ({ period, clock }) => [`P${period}`, clock].filter(Boolean).join(' '))
    .with({ league: 'mlb' }, ({ current_inning_phase: phase, current_inning: inning }) =>
      inning ? `${phase === 'top' ? 'Top' : 'Bot'} ${getOrdinalNumber(inning)}` : undefined
    )
    .otherwise(() => undefined);

export const getGameLine = (event: PublicGame | undefined, now: number): GameLine => {
  if (!event || !isPublicGameWithTeams(event)) {
    return { isLive: false, isScheduled: false };
  }

  const awayScore = event.away.points ?? event.away.runs ?? 0;
  const homeScore = event.home.points ?? event.home.runs ?? 0;

  return match(event.status)
    .returnType<GameLine>()
    .with('SCHEDULED', () => ({
      isLive: false,
      isScheduled: true,
      statusLine: formatRelativeDay(event.start_date, now),
      dateLine: formatTime(event.start_date),
    }))
    .with('IN_PROGRESS', () => ({
      isLive: true,
      isScheduled: false,
      homeScore,
      awayScore,
      statusLine: formatLiveClock(event.state),
    }))
    .with('FINISHED', 'FINALIZED', () => ({
      isLive: false,
      isScheduled: false,
      homeScore,
      awayScore,
      statusLine: 'FINAL',
      dateLine: formatShortDate(event.start_date),
    }))
    .with('CANCELED', () => ({
      isLive: false,
      isScheduled: false,
      statusLine: 'CANCELED',
      dateLine: formatShortDate(event.start_date),
    }))
    .exhaustive();
};
